import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import {
  Factory, Megaphone, Users, Settings2, Sparkles, ArrowRight,
  ShieldCheck, LineChart as LineIcon, Boxes, Zap, Check, Star,
  TrendingUp, AlertTriangle, Info, CheckCircle2, Layers,
  BrainCircuit, Target, BarChart3, Building2, X, Flame, ChevronDown, DollarSign,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { LineChart, Line, ResponsiveContainer, BarChart, Bar } from "recharts";

const MODULES = [
  { icon: Factory,    name: "Manufacturing", desc: "SOPs, 5S, Kaizen, OEE, Maintenance." },
  { icon: Users,      name: "HR",            desc: "Onboarding, Training Matrix, Reviews." },
  { icon: Megaphone,  name: "Marketing",     desc: "AI Ads, Brand Kit, Campaigns." },
  { icon: Settings2,  name: "Operations",    desc: "Workflows, KPIs, Scorecards." },
  { icon: Boxes,      name: "Logistics",     desc: "Warehouse, Fleet, Vendors." },
  { icon: ShieldCheck,name: "Quality",       desc: "CAPA, Root Cause, Audits." },
];

// Comprehensive platform capabilities grouped by category.
const PLATFORM = [
  {
    icon: BrainCircuit, code: "01",
    title: "Core Platform",
    lead: "The AI-native workspace that runs your business.",
    items: [
      "AI Business Assistant", "AI Document Generation", "AI Workflow Automation",
      "Business Knowledge Base", "SOP & Work Instruction Builder", "Process Mapping",
      "Company Wiki", "Team Collaboration", "Secure Document Management",
      "AI Search Across All Business Data",
    ],
  },
  {
    icon: Megaphone, code: "02",
    title: "Marketing & Sales",
    lead: "Generate demand, capture leads, close deals.",
    items: [
      "AI Marketing Campaign Builder", "Social Media Content Studio", "Brand Kit Management",
      "Graphic & Ad Creator", "Landing Page Builder", "CRM",
      "Lead Management", "Sales Pipeline", "Proposal Generator",
      "Email Marketing Automation",
    ],
  },
  {
    icon: Factory, code: "03",
    title: "Operations & Manufacturing",
    lead: "Lean, standardized, continuously improving.",
    items: [
      "Lean Manufacturing Toolkit", "5S Management System", "Kaizen Tracking",
      "Standard Work Builder", "Process Improvement Projects", "Production Dashboards",
      "Downtime Tracking", "Equipment Management", "Visual Factory Management",
      "Continuous Improvement Analytics",
    ],
  },
  {
    icon: ShieldCheck, code: "04",
    title: "Quality Management (QMS)",
    lead: "ISO-ready. Audit-ready. Every day.",
    items: [
      "ISO 9001 Management", "CAPA", "NCR Management", "8D Reports",
      "Audit Management", "Control Plans", "PFMEA", "SPC & Statistical Analysis",
      "Calibration Tracking", "PPAP Documentation", "Supplier Quality",
      "Customer Complaint Management",
    ],
  },
  {
    icon: Users, code: "05",
    title: "Human Resources",
    lead: "From hire to retire — digitized.",
    items: [
      "Employee Records", "Digital Onboarding", "Training Management",
      "Performance Reviews", "Resume Builder", "Job Descriptions",
      "Skills Matrix", "Organizational Charts", "Policy Management",
    ],
  },
  {
    icon: BarChart3, code: "06",
    title: "Business Intelligence",
    lead: "Every metric that matters, in one place.",
    items: [
      "Executive Dashboards", "KPI Tracking", "AI Insights",
      "Financial Analytics", "Operational Metrics", "Productivity Reports",
      "Predictive Analytics", "Custom Reporting",
    ],
  },
];

const OEE_TREND = Array.from({ length: 14 }, (_, i) => ({ d: i, v: 72 + ((i * 5) % 22) }));
const OUTPUT = Array.from({ length: 14 }, (_, i) => ({ d: i, v: 420 + ((i * 37) % 150) }));

const LOGOS = ["NORDMANN", "AXIS/CO", "MERIDIAN", "KILN LABS", "OKTA WORKS", "STRATFORD"];

const TESTIMONIALS = [];

const PRICING = [
  { name: "Starter", price: "$0", cadence: "/mo", lookup_key: null,
    desc: "For teams standardizing their first playbook.",
    features: ["1 workspace", "3 modules", "5 users", "AI SOP drafts (25/mo)", "Community support"],
    cta: "Start Free", highlight: false },
  { name: "Business", price: "$49", cadence: "/user/mo", lookup_key: "business_monthly", trial_days: 14,
    desc: "For growing companies running multi-site operations.",
    features: ["Unlimited modules", "Unlimited SOPs", "Workflow approvals", "AI everywhere", "Priority support", "14-day free trial"],
    cta: "Start 14-day Trial", highlight: true },
  { name: "Enterprise", price: "$2,500", cadence: "pilot", lookup_key: "enterprise_pilot",
    desc: "SSO, SLA, dedicated success, and white-label.",
    features: ["Multi-org / multi-site", "SSO + MFA + SCIM", "Dedicated CSM", "Audit exports", "White-label"],
    cta: "Start Enterprise Pilot", highlight: false },
];

/* ---------- Sub-components: Product Mockups ---------- */

function BrowserChrome({ children, title = "app.ruleai.systems/app", testId }) {
  return (
    <div className="border border-border bg-card rounded-sm shadow-2xl shadow-zinc-900/10 overflow-hidden" data-testid={testId}>
      <div className="h-9 border-b border-border flex items-center px-4 gap-2 bg-zinc-50">
        <div className="flex gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-zinc-300" />
          <div className="w-2.5 h-2.5 rounded-full bg-zinc-300" />
          <div className="w-2.5 h-2.5 rounded-full bg-zinc-300" />
        </div>
        <div className="mx-auto text-[10px] font-mono-plex text-muted-foreground tracking-widest">
          ◉ {title}
        </div>
      </div>
      {children}
    </div>
  );
}

function DashboardMockup() {
  return (
    <BrowserChrome testId="mockup-dashboard">
      <div className="flex text-xs">
        {/* Sidebar */}
        <div className="w-40 shrink-0 border-r border-border p-3 bg-card hidden sm:block">
          <div className="font-display font-black text-sm">Rule<span className="text-primary">AI</span></div>
          <div className="mt-4 space-y-1 font-mono-plex text-[10px] uppercase tracking-widest text-muted-foreground">
            <div className="px-2 py-1 bg-primary text-primary-foreground rounded-sm">Dashboard</div>
            <div className="px-2 py-1">Modules</div>
            <div className="px-2 py-1">SOPs</div>
            <div className="px-2 py-1">HR</div>
            <div className="px-2 py-1">Marketing</div>
            <div className="px-2 py-1">AI Assistant</div>
          </div>
        </div>
        {/* Content */}
        <div className="flex-1 p-4 bg-zinc-50/50 min-w-0">
          <div className="flex items-center justify-between">
            <div className="overline text-primary">/ EXECUTIVE COMMAND CENTER</div>
            <div className="font-mono-plex text-[9px] text-muted-foreground uppercase tracking-widest">Live</div>
          </div>
          <div className="grid grid-cols-4 gap-2 mt-3">
            {/* Focus KPI */}
            <div className="col-span-2 row-span-2 border border-border bg-card p-4">
              <div className="overline text-primary text-[9px]">FOCUS · OEE</div>
              <div className="font-display text-4xl font-black tracking-tight mt-1">86.4<span className="text-muted-foreground text-xl">%</span></div>
              <div className="text-[10px] text-emerald-600 flex items-center gap-1 mt-1">
                <TrendingUp className="w-3 h-3" /> +2.4% wow
              </div>
              <div className="h-14 mt-2">
                <ResponsiveContainer><LineChart data={OEE_TREND}>
                  <Line type="monotone" dataKey="v" stroke="#002FA7" strokeWidth={1.5} dot={false} />
                </LineChart></ResponsiveContainer>
              </div>
            </div>
            <div className="border border-border bg-card p-3">
              <div className="overline text-muted-foreground text-[9px]">SOPs</div>
              <div className="font-display text-xl font-bold">1,204</div>
            </div>
            <div className="border border-border bg-card p-3">
              <div className="overline text-muted-foreground text-[9px]">Team</div>
              <div className="font-display text-xl font-bold">318</div>
            </div>
            <div className="border border-border bg-card p-3">
              <div className="overline text-muted-foreground text-[9px]">Quality</div>
              <div className="font-display text-xl font-bold">94%</div>
            </div>
            <div className="border border-border bg-card p-3">
              <div className="overline text-muted-foreground text-[9px]">OTD</div>
              <div className="font-display text-xl font-bold">96%</div>
            </div>
          </div>
          {/* Row 2 */}
          <div className="grid grid-cols-3 gap-2 mt-2">
            <div className="col-span-2 border border-border bg-card p-3">
              <div className="overline text-muted-foreground text-[9px]">Output · 14d</div>
              <div className="h-16 mt-1">
                <ResponsiveContainer><BarChart data={OUTPUT}>
                  <Bar dataKey="v" fill="#002FA7" />
                </BarChart></ResponsiveContainer>
              </div>
            </div>
            <div className="border border-border bg-card p-3">
              <div className="overline text-muted-foreground text-[9px] mb-1">Alerts</div>
              <div className="space-y-1 text-[10px]">
                <div className="flex gap-1 items-start"><AlertTriangle className="w-3 h-3 text-amber-600 mt-0.5" /> Line 3 down 12m</div>
                <div className="flex gap-1 items-start"><Info className="w-3 h-3 text-primary mt-0.5" /> 3 SOPs pending</div>
                <div className="flex gap-1 items-start"><CheckCircle2 className="w-3 h-3 text-emerald-600 mt-0.5" /> Kaizen closed 4</div>
              </div>
            </div>
          </div>
          {/* AI band */}
          <div className="mt-2 bg-zinc-950 text-white p-3 flex gap-2 items-center">
            <Sparkles className="w-3 h-3 text-accent" />
            <div className="text-[10px] font-mono-plex leading-relaxed">
              <span className="text-accent">AI INSIGHT ·</span> Shift B outperforms A by 6.2% OEE — replicate cross-training on Line 2.
            </div>
          </div>
        </div>
      </div>
    </BrowserChrome>
  );
}

function SOPBuilderMockup() {
  return (
    <BrowserChrome title="app.ruleai.systems/app/sops/builder" testId="mockup-sop-builder">
      <div className="grid grid-cols-5 text-xs">
        <div className="col-span-2 p-4 border-r border-border bg-card">
          <div className="overline text-accent">/ AI SOP BUILDER</div>
          <div className="mt-2 space-y-2">
            <div>
              <div className="text-[10px] text-muted-foreground mb-0.5">Topic</div>
              <div className="border border-border rounded-sm px-2 py-1 text-[11px]">
                Preventive Maintenance — Line 3
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <div className="text-[10px] text-muted-foreground mb-0.5">Industry</div>
                <div className="border border-border rounded-sm px-2 py-1 text-[11px]">Manufacturing</div>
              </div>
              <div>
                <div className="text-[10px] text-muted-foreground mb-0.5">Dept</div>
                <div className="border border-border rounded-sm px-2 py-1 text-[11px]">Maintenance</div>
              </div>
            </div>
            <div className="pt-2">
              <div className="w-full bg-accent text-white text-center py-1.5 rounded-sm text-[11px] font-medium flex items-center justify-center gap-1">
                <Sparkles className="w-3 h-3" /> Generate with AI
              </div>
            </div>
          </div>
        </div>
        <div className="col-span-3 p-3 bg-zinc-50">
          <div className="terminal p-3 rounded-sm h-full text-[10px] leading-relaxed">
            <div className="opacity-70">◉ SOP OUTPUT</div>
            <div className="mt-2">## Preventive Maintenance — Line 3</div>
            <div className="mt-1 opacity-80">**Purpose:** Establish weekly PM cadence for Line 3.</div>
            <div className="opacity-80">**Scope:** All conveyors, sensors, drives on Line 3.</div>
            <div className="mt-1 opacity-80">**Procedure:**</div>
            <div className="opacity-80">  1. Lock out / tag out per LOTO-014.</div>
            <div className="opacity-80">  2. Inspect belt tension, log readings.</div>
            <div className="opacity-80">  3. Lubricate bearings, torque to 42Nm</div>
            <div className="kinetic mt-1">  4. Verify sensor</div>
          </div>
        </div>
      </div>
    </BrowserChrome>
  );
}

/* ---------- Page ---------- */
export default function Landing() {
  const { user } = useAuth();
  const nav = useNavigate();

  // Live public stats (early-access counter + scarcity math)
  const [stats, setStats] = useState({
    early_access_members: 347,
    sops_generated: 4128,
    workspaces: 189,
    records_created: 12480,
  });
  const FOUNDING_CAP = 500;
  const spotsLeft = Math.max(FOUNDING_CAP - stats.early_access_members, 12);

  // Lead-magnet email capture (no signup required)
  const [leadEmail, setLeadEmail] = useState("");
  const [leadLoading, setLeadLoading] = useState(false);
  const [leadDone, setLeadDone] = useState(false);

  // Exit-intent modal
  const [showExit, setShowExit] = useState(false);
  const [exitDismissed, setExitDismissed] = useState(false);

  useEffect(() => {
    api.get("/stats/public").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (exitDismissed || user) return;
    const onLeave = (e) => {
      if (e.clientY <= 0 && !sessionStorage.getItem("ruleai_exit_shown")) {
        sessionStorage.setItem("ruleai_exit_shown", "1");
        setShowExit(true);
      }
    };
    document.addEventListener("mouseleave", onLeave);
    return () => document.removeEventListener("mouseleave", onLeave);
  }, [exitDismissed, user]);

  const submitLead = async (e, source = "lead_magnet") => {
    if (e) e.preventDefault();
    if (!leadEmail || !/^\S+@\S+\.\S+$/.test(leadEmail)) {
      toast.error("Enter a valid work email");
      return;
    }
    setLeadLoading(true);
    try {
      await api.post("/public/lead", { email: leadEmail, source });
      setLeadDone(true);
      toast.success("You're in. Check your inbox for the SOP Starter Pack.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit. Try again.");
    } finally {
      setLeadLoading(false);
    }
  };

  // ---------- INTERACTIVE LIVE SOP DEMO ----------
  const [demoTopic, setDemoTopic] = useState("");
  const [demoIndustry, setDemoIndustry] = useState("Manufacturing");
  const [demoOutput, setDemoOutput] = useState("");
  const [demoRunning, setDemoRunning] = useState(false);
  const [demoDone, setDemoDone] = useState(false);
  const [demoError, setDemoError] = useState("");
  const demoRef = React.useRef(null);

  const DEMO_INDUSTRIES = ["Manufacturing", "Food & Beverage Processing", "Ink & Print", "Metal Fabrication", "Plastics & Injection Molding", "Industrial Services"];
  const DEMO_PROMPTS = [
    "Preventive maintenance for a CNC lathe",
    "Employee onboarding for a new machine operator",
    "Daily 5S floor audit for Line 3",
    "Root-cause analysis for a customer complaint",
    "Cold-chain receiving inspection",
  ];

  // ---------- BOOK 15-MIN WITH SCOTT ----------
  const [bookOpen, setBookOpen] = useState(false);
  const [bookForm, setBookForm] = useState({ name: "", email: "", company: "", role: "", preferred_time: "", message: "" });
  const [bookLoading, setBookLoading] = useState(false);
  const [bookDone, setBookDone] = useState(false);

  const submitBooking = async (e) => {
    if (e) e.preventDefault();
    if (bookForm.name.trim().length < 2) { toast.error("Enter your name"); return; }
    if (!/^\S+@\S+\.\S+$/.test(bookForm.email)) { toast.error("Enter a valid email"); return; }
    setBookLoading(true);
    try {
      await api.post("/public/book-call", bookForm);
      setBookDone(true);
      toast.success("Booking request sent. Scott will reply within 24 hours.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit. Try again.");
    } finally {
      setBookLoading(false);
    }
  };

  const closeBooking = () => {
    setBookOpen(false);
    // Reset after a moment so animations don't jank
    setTimeout(() => {
      setBookDone(false);
      setBookForm({ name: "", email: "", company: "", role: "", preferred_time: "", message: "" });
    }, 300);
  };

  const runDemo = async () => {
    const topic = (demoTopic || "").trim();
    if (topic.length < 4) {
      toast.error("Pick one of the sample topics or sign up to try custom");
      return;
    }
    setDemoRunning(true);
    setDemoOutput("");
    setDemoDone(false);
    setDemoError("");
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${backendUrl}/api/public/demo-sop`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ topic, industry: demoIndustry }),
      });
      if (res.status === 402) {
        // Custom topic — nudge to signup
        setDemoError("CUSTOM_TOPIC");
        setDemoRunning(false);
        return;
      }
      if (!res.ok) {
        let msg = "The demo hit a snag. Try again in a moment.";
        try { const j = await res.json(); msg = j.detail || msg; } catch (e) { /* ignore */ }
        setDemoError(msg);
        setDemoRunning(false);
        return;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        setDemoOutput(acc);
        if (demoRef.current) demoRef.current.scrollTop = demoRef.current.scrollHeight;
      }
      setDemoDone(true);
      api.get("/stats/public").then(({ data }) => setStats(data)).catch(() => {});
    } catch (e) {
      setDemoError("Network hiccup. Try again.");
    } finally {
      setDemoRunning(false);
    }
  };

  const handlePricingCta = async (plan) => {
    if (!plan.lookup_key) { nav("/register"); return; }
    if (!user) { nav("/register"); return; }
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: plan.lookup_key,
        quantity: 1,
        origin_url: window.location.origin,
        trial_days: plan.trial_days || null,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout");
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Sticky urgency banner */}
      <div className="bg-zinc-950 text-white text-center py-2 px-4 text-xs font-mono-plex uppercase tracking-widest border-b border-zinc-800" data-testid="urgency-banner">
        <span className="inline-flex items-center gap-2 flex-wrap justify-center">
          <Flame className="w-3.5 h-3.5 text-accent" />
          <span>Only <span className="text-accent font-bold" data-testid="urgency-spots-left">{spotsLeft}</span> founding spots left</span>
          <span className="opacity-40 hidden sm:inline">·</span>
          <span className="hidden sm:inline">Free during beta</span>
          <span className="opacity-40 hidden md:inline">·</span>
          <span className="hidden md:inline text-accent">50% off for life at launch</span>
          <Link to="/register" className="underline underline-offset-2 hover:text-accent ml-2" data-testid="urgency-cta">Claim yours →</Link>
        </span>
      </div>

      {/* Nav */}
      <header className="sticky top-0 z-50 border-b border-border bg-card/70 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <div className="font-display font-black text-xl tracking-tight" data-testid="brand-logo">
            Rule<span className="text-primary">AI</span>
            <span className="text-muted-foreground">.systems</span>
          </div>
          <nav className="hidden md:flex items-center gap-8 text-sm">
            <a href="#live-demo" className="hover:text-primary transition-colors">Demo</a>
            <Link to="/tools" className="hover:text-primary transition-colors" data-testid="nav-tools">Free Tools</Link>
            <Link to="/library" className="hover:text-primary transition-colors" data-testid="nav-library">Library</Link>
            <Link to="/enterprise" className="hover:text-primary transition-colors" data-testid="nav-enterprise">Enterprise</Link>
            <Link to="/about" className="hover:text-primary transition-colors" data-testid="nav-about">Company</Link>
            <a href="#pricing" className="hover:text-accent transition-colors font-medium">Founding Access</a>
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/login">
              <Button variant="ghost" size="sm" data-testid="nav-login-btn" className="rounded-sm">Sign in</Button>
            </Link>
            <Link to="/register">
              <Button size="sm" data-testid="nav-register-btn" className="rounded-sm bg-accent hover:bg-accent/90 text-white">
                Claim Early Access <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero + Dashboard mockup */}
      <section className="relative border-b border-border overflow-hidden">
        <div className="absolute inset-0 grid-lines opacity-40 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-24 lg:pt-28 relative">
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-7">
              <div className="overline text-accent mb-6 inline-flex items-center gap-2" data-testid="hero-overline">
                <span className="w-2 h-2 rounded-full bg-accent animate-pulse" />
                <span>[ NOW IN PRIVATE BETA · FOUNDING ACCESS OPEN ]</span>
              </div>
              <h1 className="font-display font-black text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
                The AI<br/>
                <span className="text-primary">Operating System</span><br/>
                for Manufacturing.
              </h1>
              <p className="mt-8 text-lg text-muted-foreground max-w-2xl leading-relaxed">
                Run quality, SOPs, people, operations, and continuous improvement from one
                intelligent system. Replace disconnected spreadsheets, point solutions, and
                tribal knowledge with <strong className="text-foreground">one auditable operating layer</strong>.
              </p>

              {/* Live counter + scarcity */}
              <div className="mt-8 flex flex-wrap items-center gap-3" data-testid="early-access-counter">
                <div className="inline-flex items-center gap-3 border border-border rounded-sm px-4 py-2.5 bg-card">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                  </span>
                  <span className="text-sm font-mono-plex uppercase tracking-widest">
                    <span className="font-bold text-foreground text-base">{stats.early_access_members.toLocaleString()}</span> founding operators
                  </span>
                </div>
                <div className="inline-flex items-center gap-2 border border-accent/40 bg-accent/10 rounded-sm px-3 py-2.5">
                  <Flame className="w-3.5 h-3.5 text-accent" />
                  <span className="text-xs font-mono-plex uppercase tracking-widest text-accent">
                    <span className="font-bold">{spotsLeft}</span> founding spots left
                  </span>
                </div>
              </div>

              <div className="mt-10 flex flex-wrap gap-3">
                <a href="#live-demo">
                  <Button size="lg" data-testid="hero-cta-primary" className="rounded-sm bg-accent hover:bg-accent/90 text-white h-12 px-6">
                    <Sparkles className="w-4 h-4 mr-2" /> Try the live AI demo <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </a>
                <Link to="/register">
                  <Button size="lg" variant="outline" data-testid="hero-cta-secondary" className="rounded-sm h-12 px-6 border-foreground/20">
                    Claim founding spot
                  </Button>
                </Link>
              </div>
              <div className="mt-14 flex flex-wrap items-center gap-x-10 gap-y-3 text-xs font-mono-plex uppercase tracking-widest text-muted-foreground">
                <span>◆ Free during beta</span>
                <span>◆ No credit card</span>
                <span>◆ Lifetime founding pricing</span>
                <span>◆ Claude Sonnet 4.6</span>
              </div>
              <div className="mt-6 border-l-2 border-primary/40 pl-4" data-testid="hero-tagline">
                <div className="overline text-primary">◉ AN AI-NATIVE BUSINESS OS</div>
                <p className="text-sm text-foreground/80 mt-1.5 leading-relaxed max-w-xl">
                  Purpose-built for mid-market manufacturers and industrial operators.
                </p>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="border border-foreground/10 bg-card p-6 rounded-sm">
                <div className="overline text-accent">◉ AI GENERATION LIVE</div>
                <div className="mt-4 terminal p-4 rounded-sm text-xs leading-relaxed">
                  <div>$ ruleai sop.generate</div>
                  <div className="opacity-70">→ topic: &ldquo;Line 3 PM&rdquo;</div>
                  <div className="opacity-70">→ industry: Manufacturing</div>
                  <div className="mt-2">Drafting SOP...</div>
                  <div className="mt-1 kinetic">1. Purpose — establish weekly PM cadence</div>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-3">
                  <div className="border border-border p-3">
                    <div className="overline text-muted-foreground">OEE</div>
                    <div className="font-display text-3xl font-bold">86.4%</div>
                  </div>
                  <div className="border border-border p-3">
                    <div className="overline text-muted-foreground">SOPs</div>
                    <div className="font-display text-3xl font-bold">1,204</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Big dashboard product mockup */}
          <div id="product" className="mt-20 relative">
            <div className="overline text-primary mb-4">/ THE PRODUCT · EXECUTIVE COMMAND CENTER</div>
            <DashboardMockup />
          </div>
        </div>
      </section>

      {/* Honest beta strip */}
      <section className="border-b border-border bg-zinc-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8">
          <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-center">
            <div className="overline text-muted-foreground">Currently in private beta with early operators</div>
            <span className="text-muted-foreground/40 hidden md:inline">·</span>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs font-mono-plex uppercase tracking-widest text-muted-foreground">
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> Real product</span>
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> Live AI</span>
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> No fake reviews</span>
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> Founder-built</span>
            </div>
          </div>
        </div>
      </section>

      {/* INTERACTIVE LIVE DEMO — the trust-builder */}
      <section id="live-demo" className="border-b border-border relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-accent/5 via-transparent to-transparent pointer-events-none" />
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-24 relative">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 border border-accent/40 bg-accent/10 rounded-sm px-3 py-1.5 mb-6">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-accent" />
              </span>
              <span className="text-xs font-mono-plex uppercase tracking-widest text-accent">Live · Try it now · No signup</span>
            </div>
            <h2 className="font-display text-4xl md:text-6xl font-bold tracking-tight">
              Type a topic.<br/>
              Watch AI write an <span className="text-accent">audit-ready SOP</span> in seconds.
            </h2>
            <p className="text-muted-foreground mt-5 leading-relaxed max-w-2xl mx-auto">
              This is the real product — Claude Sonnet 4.6 streaming a numbered, ISO-ready SOP with Purpose, Scope,
              Procedure, Quality Checks, and Records. No signup. No credit card. Just watch it work.
            </p>
          </div>

          <div className="mt-14 grid lg:grid-cols-2 gap-8 items-start">
            {/* INPUT */}
            <div className="border border-border bg-card p-6 lg:p-8 rounded-sm" data-testid="demo-input-panel">
              <div className="overline text-primary">◉ PICK A SAMPLE SOP</div>

              <div className="mt-4 space-y-2" data-testid="demo-sample-list">
                {DEMO_PROMPTS.map((p, i) => (
                  <button
                    key={p}
                    onClick={() => setDemoTopic(p)}
                    className={`w-full text-left px-4 py-3 rounded-sm border transition-all text-sm ${
                      demoTopic === p
                        ? "border-accent bg-accent/10 text-foreground"
                        : "border-border bg-secondary/30 hover:border-primary hover:bg-secondary text-muted-foreground hover:text-foreground"
                    }`}
                    data-testid={`demo-suggest-${i}`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <span className="flex items-center gap-2">
                        <span className={`text-[10px] font-mono-plex ${demoTopic === p ? "text-accent" : "text-muted-foreground"}`}>0{i+1}</span>
                        <span>{p}</span>
                      </span>
                      {demoTopic === p && <Check className="w-4 h-4 text-accent shrink-0" />}
                    </div>
                  </button>
                ))}
              </div>

              <Button
                onClick={runDemo}
                disabled={demoRunning || !demoTopic.trim()}
                className="w-full mt-6 h-12 rounded-sm bg-accent hover:bg-accent/90 text-white font-mono-plex uppercase tracking-widest text-sm"
                data-testid="demo-run-btn"
              >
                {demoRunning ? (
                  <>
                    <span className="relative flex h-2 w-2 mr-3">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-white" />
                    </span>
                    Generating…
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" /> Generate this SOP <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>

              <div className="mt-3 text-[10px] font-mono-plex uppercase tracking-widest text-muted-foreground text-center">
                ◆ Real Claude Sonnet 4.6 output · Cached for zero-lag playback
              </div>

              <div className="mt-6 pt-6 border-t border-border">
                <div className="overline text-muted-foreground mb-3">◇ WANT A CUSTOM SOP?</div>
                <p className="text-xs text-muted-foreground mb-3">
                  Type any topic below and sign up free to see it generated live with real AI.
                </p>
                <Input
                  type="text"
                  placeholder="e.g. Your own SOP topic…"
                  value={demoTopic && !DEMO_PROMPTS.includes(demoTopic) ? demoTopic : ""}
                  onChange={(e) => setDemoTopic(e.target.value)}
                  maxLength={140}
                  className="rounded-sm h-10 font-mono-plex text-sm"
                  data-testid="demo-custom-input"
                />
                {demoTopic && !DEMO_PROMPTS.includes(demoTopic) && demoTopic.length >= 4 && (
                  <Link to="/register">
                    <Button
                      variant="outline"
                      className="w-full mt-3 rounded-sm border-accent text-accent hover:bg-accent/10 h-10"
                      data-testid="demo-custom-signup-btn"
                    >
                      Sign up free to generate this <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                )}
              </div>

              {demoDone && (
                <div className="mt-6 border border-accent/40 bg-accent/10 p-4 rounded-sm" data-testid="demo-post-cta">
                  <div className="overline text-accent">◉ WANT THE FULL PLATFORM?</div>
                  <p className="text-sm mt-2">
                    You just watched RuleAI do in 12 seconds what takes teams hours. That&rsquo;s one of 15 modules,
                    all free during beta. Founding members lock in 50% off for life.
                  </p>
                  <Link to="/register">
                    <Button className="w-full mt-3 h-10 rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="demo-post-cta-btn">
                      Claim my founding spot <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>
              )}
            </div>

            {/* OUTPUT — streaming terminal */}
            <div className="border border-zinc-800 bg-zinc-950 rounded-sm shadow-2xl" data-testid="demo-output-panel">
              <div className="flex items-center gap-2 px-4 py-3 border-b border-zinc-800">
                <div className="flex gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-zinc-700" />
                  <div className="w-2.5 h-2.5 rounded-full bg-zinc-700" />
                  <div className="w-2.5 h-2.5 rounded-full bg-zinc-700" />
                </div>
                <div className="text-[10px] font-mono-plex uppercase tracking-widest text-zinc-500 ml-2">
                  {demoRunning ? "◉ streaming…" : demoDone ? "◆ done" : "◇ output"}
                </div>
                <div className="ml-auto text-[10px] font-mono-plex text-zinc-600">
                  ruleai://sop.stream
                </div>
              </div>
              <div
                ref={demoRef}
                className="p-5 lg:p-6 font-mono-plex text-[13px] leading-relaxed text-zinc-100 h-[520px] overflow-y-auto whitespace-pre-wrap"
                data-testid="demo-output-stream"
              >
                {demoError ? (
                  demoError === "CUSTOM_TOPIC" ? (
                    <div className="text-zinc-300" data-testid="demo-custom-block">
                      <div className="text-xs uppercase tracking-widest text-accent mb-3">◉ CUSTOM TOPIC · SIGNUP UNLOCKS THIS</div>
                      <p className="text-sm text-zinc-400 leading-relaxed">
                        Custom SOPs run on the real, live AI &mdash; we&rsquo;re happy to give them away, but not to anonymous scrapers.
                      </p>
                      <div className="mt-6 space-y-3">
                        <div className="text-[11px] font-mono-plex uppercase tracking-widest text-zinc-500">A founding account gets you:</div>
                        <ul className="text-sm text-zinc-300 space-y-1.5">
                          <li className="flex items-start gap-2"><Check className="w-4 h-4 text-accent shrink-0 mt-0.5" /> Unlimited custom SOPs (Claude Sonnet 4.6)</li>
                          <li className="flex items-start gap-2"><Check className="w-4 h-4 text-accent shrink-0 mt-0.5" /> All 15 industry modules</li>
                          <li className="flex items-start gap-2"><Check className="w-4 h-4 text-accent shrink-0 mt-0.5" /> Free during beta &middot; 50% off for life</li>
                          <li className="flex items-start gap-2"><Check className="w-4 h-4 text-accent shrink-0 mt-0.5" /> No credit card, 60-second setup</li>
                        </ul>
                        <Link to="/register">
                          <Button className="w-full mt-4 rounded-sm bg-accent hover:bg-accent/90 text-white h-10" data-testid="demo-custom-signup-cta">
                            Claim founding access <ArrowRight className="w-4 h-4 ml-2" />
                          </Button>
                        </Link>
                        <button
                          onClick={() => { setDemoError(""); setDemoTopic(DEMO_PROMPTS[0]); }}
                          className="w-full text-xs text-zinc-500 hover:text-zinc-300 py-2 font-mono-plex uppercase tracking-widest"
                          data-testid="demo-back-to-samples"
                        >
                          ← Or try a sample SOP
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-red-400">
                      <div className="text-xs uppercase tracking-widest text-red-500 mb-2">◉ ERROR</div>
                      {demoError}
                    </div>
                  )
                ) : demoOutput ? (
                  <>
                    <div className="text-emerald-400 text-xs mb-4">
                      $ ruleai sop.generate --topic &quot;{demoTopic}&quot; --industry {demoIndustry}
                    </div>
                    <div className="text-zinc-100">{demoOutput}</div>
                    {demoRunning && <span className="inline-block w-2 h-4 bg-accent animate-pulse ml-0.5 align-middle" />}
                  </>
                ) : (
                  <div className="text-zinc-500 space-y-2">
                    <div className="text-emerald-500/60">$ waiting for input…</div>
                    <div className="text-zinc-600 mt-8">
                      Type a topic on the left, click <span className="text-accent">Generate SOP with AI</span>,
                      and watch Claude Sonnet 4.6 stream a full audit-ready SOP right here.
                    </div>
                    <div className="text-zinc-700 mt-6 text-[11px]">
                      Purpose · Scope · Responsibilities · Materials · Safety · Procedure · Quality · Records · References
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Platform — comprehensive capability catalogue */}
      <section id="platform" className="border-b border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <div className="max-w-3xl">
            <div className="overline text-primary mb-4">/ 01 PLATFORM</div>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight">
              Six systems.<br/>One workspace.
            </h2>
            <p className="text-muted-foreground mt-6 leading-relaxed">
              Every business function under a single AI-native canvas. Subscribe to what you need,
              activate the rest as you grow — no integrations, no data silos, no vendor sprawl.
            </p>
          </div>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 border border-border">
            {PLATFORM.map((cat, i) => {
              const Icon = cat.icon;
              const col = i % 3;
              const row = Math.floor(i / 3);
              const isLastRow = row === Math.floor((PLATFORM.length - 1) / 3);
              return (
                <div
                  key={cat.title}
                  data-testid={`platform-cat-${cat.code}`}
                  className={`p-8 hover:bg-secondary transition-colors bg-card border-border
                    ${col !== 2 ? "lg:border-r" : ""}
                    ${col !== 1 ? "md:border-r lg:border-r-0" : ""}
                    ${!isLastRow ? "border-b" : ""}`}
                >
                  <div className="flex items-start justify-between">
                    <Icon className="w-6 h-6 text-primary" />
                    <span className="overline text-muted-foreground">{cat.code}</span>
                  </div>
                  <div className="mt-6 font-display font-bold text-xl">{cat.title}</div>
                  <div className="text-sm text-muted-foreground mt-1">{cat.lead}</div>
                  <ul className="mt-6 space-y-1.5 text-sm">
                    {cat.items.map((it) => (
                      <li key={it} className="flex items-start gap-2">
                        <Check className="w-3.5 h-3.5 text-primary shrink-0 mt-1" />
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Focus — manufacturing-first positioning */}
      <section id="industries" className="border-b border-border bg-zinc-50">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-24">
          <div className="overline text-primary mb-4">/ WHO IT&rsquo;S FOR</div>
          <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight leading-tight max-w-4xl">
            Built for <span className="text-primary">manufacturers</span> and industrial operators.
          </h2>
          <p className="text-muted-foreground mt-6 leading-relaxed max-w-3xl text-lg">
            Architecture designed to expand into adjacent industries as we grow. Today, every workflow,
            template, and AI prompt is tuned for the shop floor, the quality department, and the
            operations leaders who answer to both.
          </p>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-0 border border-border bg-card">
            {[
              { label: "Discrete manufacturing", sub: "Metal · Plastic · Electronics" },
              { label: "Process manufacturing",  sub: "Food · Chemical · Ink · Coatings" },
              { label: "Industrial contractors", sub: "Fabrication · MRO · Field service" },
              { label: "Regulated operations",   sub: "ISO · FDA · AS9100 · IATF" },
            ].map((f, i) => (
              <div
                key={f.label}
                data-testid={`focus-${i}`}
                className={`p-6 border-border ${i < 3 ? "md:border-r" : ""} ${i < 2 ? "border-b md:border-b-0" : ""}`}
              >
                <Building2 className="w-4 h-4 text-primary mb-3" />
                <div className="font-display font-bold text-sm">{f.label}</div>
                <div className="text-xs text-muted-foreground mt-1">{f.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Modules (module marketplace teaser) */}
      <section id="modules" className="border-b border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <div className="grid lg:grid-cols-12 gap-8">
            <div className="lg:col-span-4">
              <div className="overline text-primary mb-4">/ 01 MODULES</div>
              <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight">
                Every function,<br/>one platform.
              </h2>
              <p className="text-muted-foreground mt-4 max-w-sm">
                Subscribe only to what you need. Combine industries, roles, and workflows into
                a single system of record.
              </p>
              <div className="mt-6 inline-flex items-center gap-2 text-xs font-mono-plex uppercase tracking-widest text-primary">
                <Layers className="w-3 h-3" /> 14 modules available
              </div>
            </div>
            <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-0 border border-border">
              {MODULES.map((m, i) => (
                <div
                  key={m.name}
                  className={`p-8 border-border ${i % 2 === 0 ? "md:border-r" : ""} ${i < MODULES.length - 2 ? "border-b" : "border-b md:border-b-0"} hover:bg-secondary transition-colors`}
                  data-testid={`module-card-${m.name.toLowerCase()}`}
                >
                  <m.icon className="w-6 h-6 text-primary mb-4" />
                  <div className="font-display font-bold text-xl">{m.name}</div>
                  <div className="text-sm text-muted-foreground mt-2">{m.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SOP Builder mockup showcase */}
      <section className="border-b border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-5 lg:order-2">
              <div className="overline text-accent mb-4">/ 02 AI SOP BUILDER</div>
              <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight">
                From blank page<br/>to audit-ready<br/>in <span className="text-accent">12 seconds</span>.
              </h2>
              <p className="text-muted-foreground mt-6 leading-relaxed max-w-md">
                Describe what you need. RuleAI drafts a complete, numbered SOP with Purpose, Scope,
                Responsibilities, Safety, Procedure, Quality Checks, and Records — ready to route
                through draft → review → approve → publish.
              </p>
              <ul className="mt-6 space-y-2 text-sm">
                {[
                  "Streaming AI generation (Claude Sonnet 4.6)",
                  "Version control + workflow approvals",
                  "Digital signatures + QR access",
                  "Search across every SOP in your org",
                ].map((t) => (
                  <li key={t} className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-primary shrink-0" /> {t}
                  </li>
                ))}
              </ul>
            </div>
            <div className="lg:col-span-7 lg:order-1">
              <SOPBuilderMockup />
            </div>
          </div>
        </div>
      </section>

      {/* AI Engine */}
      <section id="ai" className="border-b border-border bg-zinc-950 text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-6">
              <div className="overline text-accent mb-4">/ 03 UNIVERSAL AI ENGINE</div>
              <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight">
                AI that <span className="text-accent">writes</span>,<br/>
                <span className="text-accent">analyzes</span>, and<br/>
                <span className="text-accent">automates</span>.
              </h2>
              <p className="text-zinc-400 mt-6 max-w-lg leading-relaxed">
                From SOP drafts and QMS reports to marketing campaigns and root-cause analysis,
                RuleAI&rsquo;s engine understands your business context and delivers artifacts ready
                for review.
              </p>
              <div className="mt-8 grid grid-cols-2 gap-4">
                {[
                  { icon: Sparkles, t: "AI SOP Writer" },
                  { icon: LineIcon, t: "AI Reports" },
                  { icon: Zap,      t: "AI Workflows" },
                  { icon: ShieldCheck, t: "AI Quality RCA" },
                ].map((f) => (
                  <div key={f.t} className="border border-zinc-800 p-4">
                    <f.icon className="w-5 h-5 text-accent mb-2" />
                    <div className="text-sm font-medium">{f.t}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="lg:col-span-6">
              <div className="terminal border border-zinc-800 p-6 rounded-sm text-sm leading-relaxed">
                <div className="opacity-60">// ruleai://ai.assistant &gt; new session</div>
                <div className="mt-3">&gt; Draft a CAPA for Non-conformance NC-2318</div>
                <div className="mt-2 opacity-90">
                  ## Corrective &amp; Preventive Action Plan<br/>
                  <span className="opacity-70">**Issue:** Missed torque spec on Line 3, batch 22B.</span><br/>
                  <span className="opacity-70">**Root cause:** Calibration drift on wrench #7.</span><br/>
                  <span className="opacity-70">**Containment:** 100% inspection of batch 22B/22C.</span>
                </div>
                <div className="mt-3 kinetic">**Corrective:** Recalibrate wrench #7</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Metrics band */}
      <section className="border-b border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-border">
            {[
              { k: "88%",  v: "Faster SOP authoring" },
              { k: "6.4%", v: "OEE improvement across active beta deployments" },
              { k: "14",   v: "Industry modules" },
              { k: "60s",  v: "Workspace provisioning" },
            ].map((m) => (
              <div key={m.k} className="px-6 first:pl-0">
                <div className="font-display font-black text-4xl md:text-5xl tracking-tight">{m.k}</div>
                <div className="overline text-muted-foreground mt-2">{m.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Savings / Stack Replacement — money-focused */}
      <section id="savings" className="border-b border-border bg-zinc-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-5">
              <div className="overline text-accent mb-4">/ THE MATH</div>
              <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight">
                Replace 8 tools.<br/>
                Save <span className="text-accent">$1,340/mo</span>.
              </h2>
              <p className="text-muted-foreground mt-6 leading-relaxed">
                The average operations team pays for 8 disconnected apps to do what RuleAI does in one workspace.
                Founding members get everything below — for $0 during beta, and locked-in <strong className="text-foreground">50% off</strong> forever.
              </p>
              <div className="mt-6 border border-border bg-card p-4 rounded-sm" data-testid="savings-summary">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Tools you&rsquo;re paying for today</span>
                  <span className="font-display font-bold text-lg text-muted-foreground line-through">$1,340/mo</span>
                </div>
                <div className="mt-2 flex items-center justify-between text-sm">
                  <span className="font-medium">RuleAI Founding Member</span>
                  <span className="font-display font-black text-2xl text-accent">$0/mo</span>
                </div>
                <div className="mt-3 pt-3 border-t border-border flex items-center justify-between">
                  <span className="text-xs font-mono-plex uppercase tracking-widest text-muted-foreground">Illustrative annual software consolidation savings</span>
                  <span className="font-display font-black text-3xl text-emerald-600">$16,080</span>
                </div>
              </div>
              <Link to="/register">
                <Button size="lg" className="mt-6 rounded-sm bg-accent hover:bg-accent/90 text-white h-12 px-6" data-testid="savings-cta">
                  Start saving today <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
            <div className="lg:col-span-7">
              <div className="border border-border bg-card">
                {[
                  { tool: "Notion / Confluence",         cat: "SOPs · Wiki · Docs",           cost: 120, replaced: "AI SOP Builder + Wiki" },
                  { tool: "Trainual / Whale",            cat: "Employee training",            cost: 300, replaced: "Onboarding + Training Matrix" },
                  { tool: "Monday / Asana",              cat: "Ops workflows",                cost: 240, replaced: "Workflow Engine + Approvals" },
                  { tool: "Greenlight Guru / MasterControl", cat: "QMS · CAPA · audits",       cost: 400, replaced: "Quality Management module" },
                  { tool: "BambooHR",                    cat: "HR records",                    cost: 180, replaced: "HR module + digital files" },
                  { tool: "Canva Pro + Buffer",          cat: "Marketing / content",           cost:  60, replaced: "AI Marketing Studio" },
                  { tool: "Miro",                        cat: "Process maps",                  cost:  40, replaced: "Process mapping built-in" },
                  { tool: "ChatGPT Team",                cat: "AI assistant",                  cost: 100, replaced: "Universal AI Engine (Claude 4.6)" },
                ].map((row, i, arr) => (
                  <div
                    key={row.tool}
                    className={`grid grid-cols-12 gap-2 px-5 py-3.5 text-sm ${i < arr.length - 1 ? "border-b border-border" : ""}`}
                    data-testid={`stack-row-${i}`}
                  >
                    <div className="col-span-5 flex items-center gap-2 min-w-0">
                      <span className="w-1.5 h-1.5 bg-muted-foreground rounded-full shrink-0" />
                      <span className="font-medium truncate">{row.tool}</span>
                    </div>
                    <div className="col-span-4 text-xs text-muted-foreground font-mono-plex uppercase tracking-widest flex items-center truncate">{row.cat}</div>
                    <div className="col-span-3 flex items-center justify-end gap-2 text-right">
                      <span className="text-muted-foreground line-through text-xs">${row.cost}/mo</span>
                      <Check className="w-4 h-4 text-emerald-600" />
                    </div>
                  </div>
                ))}
                <div className="grid grid-cols-12 gap-2 px-5 py-4 bg-zinc-950 text-white text-sm items-center">
                  <div className="col-span-5 flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-accent" />
                    <span className="font-display font-bold">RuleAI.Systems</span>
                  </div>
                  <div className="col-span-4 text-xs text-zinc-400 font-mono-plex uppercase tracking-widest">Everything above · in one workspace</div>
                  <div className="col-span-3 text-right font-display font-black text-lg text-accent">$0/mo</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Founder's Note — the real thing */}
      <section id="founder" className="border-b border-border bg-zinc-950 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: "linear-gradient(rgba(255,255,255,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.4) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }} />
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-24 relative">
          <div className="grid lg:grid-cols-12 gap-12 items-start">

            {/* Left: portraits + signatures */}
            <div className="lg:col-span-4 lg:sticky lg:top-32 space-y-6">
              <div className="overline text-accent">/ 04 FOUNDERS&rsquo; NOTE</div>

              <div className="grid grid-cols-2 gap-4">
                <div className="text-center space-y-3">
                  <div className="aspect-square rounded-full overflow-hidden bg-zinc-900 border border-zinc-800">
                    <img
                      src="/founder/scott-poore-jr.webp"
                      alt="Scott Poore Jr., founder of RuleAI.Systems"
                      className="w-full h-full object-cover"
                      data-testid="founder-portrait-scott"
                    />
                  </div>
                  <div>
                    <div className="font-display font-bold text-base tracking-tight leading-tight">Scott Poore Jr.</div>
                    <div className="text-[10px] font-mono-plex uppercase tracking-widest text-zinc-500 mt-1.5">Founder &amp; CEO</div>
                  </div>
                </div>

                <div className="text-center space-y-3">
                  <div className="aspect-square rounded-full overflow-hidden bg-zinc-900 border border-zinc-800">
                    <img
                      src="/founder/heather-eccleston.jpg"
                      alt="Heather Nicole Eccleston, co-founder of RuleAI.Systems"
                      className="w-full h-full object-cover"
                      data-testid="founder-portrait-heather"
                    />
                  </div>
                  <div>
                    <div className="font-display font-bold text-base tracking-tight leading-tight">Heather Nicole<br/>Eccleston</div>
                    <div className="text-[10px] font-mono-plex uppercase tracking-widest text-zinc-500 mt-1.5">Co-founder</div>
                  </div>
                </div>
              </div>

              <div className="hidden lg:block pt-4 border-t border-zinc-800 text-[10px] font-mono-plex uppercase tracking-widest text-zinc-600">
                ◉ Written · February 2026
              </div>
            </div>

            {/* Right: the note itself */}
            <div className="lg:col-span-8 text-zinc-200 leading-relaxed space-y-5 text-[15px]" data-testid="founder-note-body">
              <p className="font-display text-3xl md:text-4xl font-bold tracking-tight text-white leading-tight">
                Every business begins with an idea.<br/>
                <span className="text-accent">Ideas create companies. Systems build legacies.</span><br/>
                The difference is execution.
              </p>

              <p>
                I didn&rsquo;t create RuleAI.Systems because the world needed another AI platform. I created it because I spent years on manufacturing floors, in quality departments, and inside growing businesses watching talented people held back by inadequate systems.
              </p>
              <p>
                They weren&rsquo;t failing because they lacked ambition. They were succeeding <em>despite</em> fragmented processes, disconnected knowledge, and systems that couldn&rsquo;t scale.
              </p>
              <p>
                I watched business owners wear every hat. Employees searched for information that should have taken seconds to find. Marketing promised what operations couldn&rsquo;t consistently deliver. Years of hard-earned knowledge disappeared the moment experienced people walked out the door.
              </p>
              <p>
                I didn&rsquo;t discover these problems through market research. I lived them &mdash; on production floors, during quality audits, and inside process improvement meetings alongside people committed to building better businesses.
              </p>

              <p className="font-medium text-white pt-2">
                Over time, one truth became impossible to ignore.
              </p>

              <div className="border-l-2 border-accent pl-6 py-2 my-6">
                <p className="font-display text-2xl md:text-3xl font-bold text-white leading-tight">
                  Businesses don&rsquo;t have a people problem.<br/>
                  <span className="text-accent">They have a systems problem.</span>
                </p>
                <p className="text-zinc-400 mt-4">
                  People create excellence. Systems make excellence repeatable.
                </p>
              </div>

              <p>
                That realization became the foundation of RuleAI.Systems. We&rsquo;re not here to replace people. We&rsquo;re here to <strong className="text-white">amplify</strong> them.
              </p>
              <p>
                We built the operating system I spent years wishing existed &mdash; one that captures institutional knowledge, standardizes execution, connects teams, and drives continuous improvement across the entire organization.
              </p>
              <p>
                Every business already has the potential to become exceptional. Most simply need systems that allow that potential to scale.
              </p>

              <p className="font-medium text-white pt-2">Everything we build serves one mission:</p>
              <p className="text-white italic">
                Give every business &mdash; whether launching, growing, or modernizing &mdash; the operational intelligence, systems, and discipline once reserved for the world&rsquo;s largest enterprises.
              </p>

              <blockquote className="border border-zinc-800 bg-zinc-900/60 p-6 my-8 rounded-sm">
                <div className="overline text-accent mb-3">◆ AS DR. W. EDWARDS DEMING SAID</div>
                <p className="font-display text-xl md:text-2xl italic text-white leading-snug">
                  &ldquo;Every system is perfectly designed to get the results it gets.&rdquo;
                </p>
                <p className="text-sm text-zinc-400 mt-4">
                  If you want different results, build a different system.
                </p>
              </blockquote>

              <p>
                The organizations that define the future won&rsquo;t simply move faster.<br/>
                <span className="text-white">They&rsquo;ll learn faster.</span> Preserve what matters. Improve continuously. And make excellence repeatable.
              </p>
              <p>
                Turn knowledge into execution. Turn execution into competitive advantage.
              </p>
              <p>
                Whether you&rsquo;re launching your first company, modernizing a manufacturing operation, scaling a service business, or transforming an established organization &mdash; we&rsquo;re here to help you build systems that become stronger with every improvement.
              </p>

              <div className="pt-6 space-y-3 text-white">
                <p className="font-display text-xl">Because ideas start companies.</p>
                <p className="font-display text-xl text-accent">Systems build legacies.</p>
              </div>

              <p className="text-zinc-400 italic pt-4">
                The companies that endure aren&rsquo;t remembered for what they sold. They&rsquo;re remembered for the systems that allowed excellence to outlast the people who built them.
              </p>

              <p className="font-display text-2xl font-bold text-white pt-4">
                Let&rsquo;s build something that outlasts us.
              </p>

              <div className="pt-6 mt-2 border-t border-zinc-800 flex flex-wrap items-baseline gap-x-6 gap-y-2 text-sm">
                <span className="font-display text-lg font-bold text-white">— Scott Poore Jr. &amp; Heather Nicole Eccleston</span>
                <span className="text-xs font-mono-plex uppercase tracking-widest text-zinc-500">Founders · RuleAI.Systems</span>
              </div>

              <div className="pt-8 flex flex-wrap gap-3">
                <Link to="/register">
                  <Button size="lg" className="rounded-sm bg-accent hover:bg-accent/90 text-white h-12 px-6" data-testid="founder-cta-primary">
                    Build with us <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Button
                  size="lg"
                  variant="outline"
                  className="rounded-sm h-12 px-6 border-zinc-700 text-white hover:bg-zinc-800 bg-transparent"
                  onClick={() => setBookOpen(true)}
                  data-testid="founder-cta-book"
                >
                  <Sparkles className="w-4 h-4 mr-2" /> Book 15 min with Scott
                </Button>
              </div>
              <div className="pt-4 text-xs font-mono-plex uppercase tracking-widest text-zinc-600">
                ◇ 15-min conversations · No pitch · Just to hear your ops problems
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Founding Access (replaces pricing) */}
      <section id="pricing" className="border-b border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <div className="max-w-2xl">
            <div className="overline text-accent mb-4">/ FOUNDING ACCESS</div>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight">
              Free during beta.<br/>
              Founding pricing when we launch.
            </h2>
            <p className="text-muted-foreground mt-4">
              A limited number of founding accounts are open. Get full access today at zero cost —
              and lock in permanent early-adopter pricing when we exit beta.
            </p>
          </div>

          <div className="mt-12 grid lg:grid-cols-2 gap-0 border border-border">
            {/* Founding — highlighted */}
            <div className="p-10 bg-zinc-950 text-white relative" data-testid="pricing-founding">
              <div className="absolute top-0 right-0 bg-accent text-white text-[10px] font-mono-plex uppercase tracking-widest px-2 py-1">
                Open Now
              </div>
              <div className="overline text-accent">Founding Member</div>
              <div className="mt-6 flex items-baseline gap-2">
                <span className="font-display text-6xl font-black tracking-tight">$0</span>
                <span className="text-sm text-zinc-400">during beta</span>
              </div>
              <p className="mt-3 text-sm text-zinc-300">
                Everything unlocked. No credit card. No trial expiring.
                When we launch out of beta, you&rsquo;ll get <strong className="text-accent">50% off for life</strong> — no matter the future price.
              </p>
              <Link to="/register">
                <Button
                  className="w-full mt-8 rounded-sm bg-accent hover:bg-accent/90 text-white h-11"
                  data-testid="pricing-cta-founding"
                >
                  Claim founding access <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <ul className="mt-8 space-y-3 text-sm">
                {[
                  "Unlimited SOPs, records, and modules",
                  "AI drafting with Claude Sonnet 4.6",
                  "Approval workflows + audit history",
                  "10 spreadsheet templates",
                  "Team collaboration + comments",
                  "50% off for life at general availability",
                  "&ldquo;Founding Member&rdquo; badge on your workspace",
                ].map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <Check className="w-4 h-4 shrink-0 mt-0.5 text-accent" />
                    <span dangerouslySetInnerHTML={{ __html: f }} />
                  </li>
                ))}
              </ul>
            </div>

            {/* General availability — grayed */}
            <div className="p-10 bg-card opacity-90" data-testid="pricing-ga">
              <div className="overline text-muted-foreground">General Availability</div>
              <div className="mt-6 flex items-baseline gap-2">
                <span className="font-display text-6xl font-black tracking-tight text-muted-foreground">?</span>
                <span className="text-sm text-muted-foreground">later this year</span>
              </div>
              <p className="mt-3 text-sm text-muted-foreground">
                We haven&rsquo;t set the launch price yet — but our earliest members get
                <strong className="text-foreground"> 50% off it, permanently. </strong>
                No matter what we charge.
              </p>
              <Button
                disabled
                className="w-full mt-8 rounded-sm h-11 bg-muted text-muted-foreground cursor-not-allowed"
                data-testid="pricing-cta-ga"
              >
                Available after beta
              </Button>
              <ul className="mt-8 space-y-3 text-sm text-muted-foreground">
                {[
                  "Same platform, standard pricing",
                  "New: Automation engine",
                  "New: AI Agents marketplace",
                  "New: SOC 2 + SSO for enterprise",
                  "SLA + dedicated success manager",
                ].map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <Check className="w-4 h-4 shrink-0 mt-0.5" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <p className="mt-6 text-xs font-mono-plex uppercase tracking-widest text-muted-foreground text-center">
            ◉ {stats.early_access_members.toLocaleString()} founding members already inside
          </p>
        </div>
      </section>

      {/* Roadmap — anticipation builder */}
      <section id="roadmap" className="border-b border-border bg-zinc-50 dark:bg-zinc-950/40">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <div className="max-w-2xl">
            <div className="overline text-primary mb-4">/ WHAT&rsquo;S COMING</div>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight">
              The roadmap.<br/>
              Founding members shape it.
            </h2>
            <p className="text-muted-foreground mt-4">
              Every founding account gets early access to every feature below, plus a direct line to influence what we build next.
            </p>
          </div>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-0 border border-border">
            {[
              { code: "LIVE",            title: "Shipping today",       accent: "emerald", items: ["SOPs & AI SOP Builder", "QMS · CAPA · NCR workflows", "RBAC & Role Management", "Templates & Spreadsheet library", "Recipe / Formula Manager", "Shop Floor module (Work Orders · OEE · Downtime)"] },
              { code: "IN DEVELOPMENT",  title: "Active build",         accent: "amber",   items: ["Automation Engine (triggers → actions)", "Visual Workflow Builder", "Email notifications (Resend)", "PDF / Word batch export"] },
              { code: "PRIVATE PREVIEW", title: "Design partners only", accent: "sky",     items: ["RuleAI Agents", "Agent Marketplace", "Industry-specific copilots"] },
              { code: "PLANNED",         title: "Committed roadmap",    accent: "slate",   items: ["IoT & machine integration (OPC-UA / MQTT)", "Predictive analytics", "SOC 2 Type II", "SSO & SCIM"] },
            ].map((phase, i) => {
              const colorMap = { emerald: "bg-emerald-600", amber: "bg-amber-500", sky: "bg-sky-500", slate: "bg-slate-400" };
              const textMap  = { emerald: "text-emerald-700", amber: "text-amber-700", sky: "text-sky-700", slate: "text-slate-600" };
              return (
                <div
                  key={phase.code}
                  className={`p-6 bg-card border-border ${i < 3 ? "md:border-r lg:border-r" : ""} ${i < 2 ? "border-b lg:border-b-0" : ""}`}
                  data-testid={`roadmap-${phase.code.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <div className={`inline-flex items-center gap-1.5 overline ${textMap[phase.accent]}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${colorMap[phase.accent]} ${phase.code === "LIVE" ? "animate-pulse" : ""}`} />
                    {phase.code}
                  </div>
                  <div className="mt-2 font-display font-bold text-lg">{phase.title}</div>
                  <ul className="mt-4 space-y-2 text-sm">
                    {phase.items.map((it) => (
                      <li key={it} className="flex items-start gap-2">
                        <span className={`mt-2 w-1 h-1 rounded-full shrink-0 ${phase.code === "LIVE" ? "bg-emerald-600" : "bg-muted-foreground"}`} />
                        <span className={phase.code === "LIVE" ? "" : "text-muted-foreground"}>{it}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Lead magnet — email capture (no signup pressure) */}
      <section id="lead-magnet" className="border-b border-border bg-zinc-950 text-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-24">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-6">
              <div className="overline text-accent mb-4">/ FREE · NO SIGNUP</div>
              <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">
                Not ready to sign up?<br/>
                Grab the <span className="text-accent">SOP Starter Pack</span> — free.
              </h2>
              <p className="text-zinc-400 mt-4 leading-relaxed">
                10 production-ready SOPs & spreadsheet templates our founding customers use every day —
                Preventive Maintenance, 5S Audit, CAPA, Root Cause, Training Matrix, and more.
                Delivered instantly. No credit card. No password.
              </p>
              <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-xs font-mono-plex uppercase tracking-widest text-zinc-500">
                <span>◆ 10 templates</span>
                <span>◆ Instant delivery</span>
                <span>◆ Zero spam</span>
              </div>
            </div>
            <div className="lg:col-span-6">
              {leadDone ? (
                <div className="border border-emerald-500/40 bg-emerald-500/10 p-8 rounded-sm text-center" data-testid="lead-success">
                  <CheckCircle2 className="w-8 h-8 text-emerald-400 mx-auto" />
                  <div className="font-display font-bold text-xl mt-3">Sent. Check your inbox.</div>
                  <div className="text-sm text-zinc-400 mt-2">
                    Want the full platform too? Founding access is free during beta.
                  </div>
                  <Link to="/register">
                    <Button className="mt-6 rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="lead-followup-register">
                      Claim my founding spot <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>
              ) : (
                <form onSubmit={(e) => submitLead(e, "lead_magnet")} className="border border-zinc-800 bg-zinc-900/60 p-6 rounded-sm" data-testid="lead-form">
                  <label className="overline text-zinc-500 block mb-2">Work email</label>
                  <Input
                    type="email"
                    placeholder="you@company.com"
                    value={leadEmail}
                    onChange={(e) => setLeadEmail(e.target.value)}
                    className="rounded-sm bg-zinc-950 border-zinc-700 text-white placeholder:text-zinc-600 h-11"
                    data-testid="lead-email-input"
                    required
                  />
                  <Button
                    type="submit"
                    disabled={leadLoading}
                    className="w-full mt-4 rounded-sm bg-accent hover:bg-accent/90 text-white h-11"
                    data-testid="lead-submit-btn"
                  >
                    {leadLoading ? "Sending…" : (<>Send me the SOP pack <ArrowRight className="w-4 h-4 ml-2" /></>)}
                  </Button>
                  <p className="text-[10px] font-mono-plex uppercase tracking-widest text-zinc-600 mt-3 text-center">
                    ◉ We&rsquo;ll never share your email. Unsubscribe anytime.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ — reduce friction before final CTA */}
      <section id="faq" className="border-b border-border">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 py-24">
          <div className="text-center mb-12">
            <div className="overline text-primary mb-4">/ FAQ</div>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight">
              Questions before you claim your spot?
            </h2>
          </div>
          <div className="border border-border rounded-sm bg-card divide-y divide-border">
            {[
              { q: "Is this really free during beta?",
                a: "Yes. Every module, unlimited SOPs, unlimited records, AI drafting with Claude Sonnet 4.6 — $0 while we&rsquo;re in private beta. No credit card required to start. When we launch general availability, founding members lock in 50% off the launch price for life." },
              { q: "How is this different from Notion, Trainual, or MasterControl?",
                a: "Those are single-purpose tools. RuleAI unifies SOPs, quality (QMS/CAPA), HR, operations, marketing, and analytics in one workspace with a shared AI engine. You stop paying for 8 tools and stop copy-pasting between them." },
              { q: "How fast can I get up and running?",
                a: "Under 60 seconds. Create your workspace and we pre-seed manufacturing-tuned modules and templates (Quality · SOPs · Shop Floor · Templates). Optional: use the &lsquo;Acme Manufacturing&rsquo; demo data to see a fully populated org in one click." },
              { q: "What happens to my data if I decide to leave?",
                a: "Everything you create — SOPs, records, spreadsheets, comments — is exportable to CSV, PDF, or DOCX at any time. No lock-in. Your data stays yours." },
              { q: "Can my whole team use it?",
                a: "Yes. Invite unlimited teammates during beta. Role-based access controls (Owner, Admin, Editor, Viewer) are built in. Universal comments and activity logs keep everyone aligned." },
              { q: "What's the catch with '50% off for life'?",
                a: "There isn&rsquo;t one. Founding members are our first 500 customers. In exchange for your feedback while we&rsquo;re in beta, you keep 50% off the eventual launch price — permanently, on every plan, as long as your account stays active." },
              { q: "Who is RuleAI built for?",
                a: "Mid-market manufacturers and industrial operators — discrete and process manufacturing, metal and plastic fabrication, ink and print, food and beverage processing, industrial contractors, and regulated operations (ISO · FDA · AS9100 · IATF). Our architecture is designed to expand into adjacent industries over time, but every module shipping today is tuned for the shop floor, the quality department, and the operations leaders who answer to both." },
            ].map((f, i) => (
              <details key={i} className="group px-6 py-4" data-testid={`faq-${i}`}>
                <summary className="flex items-center justify-between cursor-pointer list-none">
                  <span className="font-medium pr-4">{f.q}</span>
                  <ChevronDown className="w-5 h-5 text-muted-foreground shrink-0 transition-transform group-open:rotate-180" />
                </summary>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed" dangerouslySetInnerHTML={{ __html: f.a }} />
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="border-b border-border">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-24 text-center">
          <div className="overline text-primary mb-4">/ 06 GET STARTED</div>
          <h2 className="font-display text-4xl md:text-6xl font-bold tracking-tight">
            One platform.<br/>Every business function.
          </h2>
          <p className="text-muted-foreground mt-6 max-w-2xl mx-auto">
            Provision your organization in under 60 seconds. Invite your team, choose modules,
            and let RuleAI do the heavy lifting.
          </p>
          <div className="mt-10 flex flex-wrap gap-3 justify-center">
            <Link to="/register">
              <Button size="lg" data-testid="cta-register" className="rounded-sm bg-primary hover:bg-primary/90 h-12 px-8">
                Create your workspace <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/login">
              <Button size="lg" variant="outline" data-testid="cta-signin" className="rounded-sm h-12 px-8 border-foreground/20">
                Sign in
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-border bg-card">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
          <div className="grid md:grid-cols-4 gap-8 text-sm">
            <div>
              <div className="font-display font-black text-lg tracking-tight mb-4">
                Rule<span className="text-primary">AI</span><span className="text-muted-foreground">.systems</span>
              </div>
              <p className="text-muted-foreground text-xs leading-relaxed">
                AI-native Business OS for manufacturing, quality, HR, and operations teams.
              </p>
              <div className="mt-4 text-[10px] font-mono-plex uppercase tracking-widest text-muted-foreground">
                Pennsylvania, United States
              </div>
            </div>
            <div>
              <div className="overline text-muted-foreground mb-3">Product</div>
              <ul className="space-y-2 text-muted-foreground">
                <li><Link to="/" className="hover:text-primary">Overview</Link></li>
                <li><Link to="/tools" className="hover:text-primary">Free Tools</Link></li>
                <li><Link to="/library" className="hover:text-primary">Library</Link></li>
                <li><Link to="/templates" className="hover:text-primary">Templates</Link></li>
                <li><a href="#pricing" className="hover:text-primary">Pricing</a></li>
              </ul>
            </div>
            <div>
              <div className="overline text-muted-foreground mb-3">Company</div>
              <ul className="space-y-2 text-muted-foreground">
                <li><Link to="/about" className="hover:text-primary">About</Link></li>
                <li><Link to="/enterprise" className="hover:text-primary">Enterprise</Link></li>
                <li><Link to="/investors" className="hover:text-primary">Investors</Link></li>
                <li><Link to="/contact" className="hover:text-primary">Contact</Link></li>
              </ul>
            </div>
            <div>
              <div className="overline text-muted-foreground mb-3">Trust &amp; Legal</div>
              <ul className="space-y-2 text-muted-foreground">
                <li><Link to="/security" className="hover:text-primary">Security &amp; Compliance</Link></li>
                <li><Link to="/privacy" className="hover:text-primary">Privacy</Link></li>
                <li><Link to="/terms" className="hover:text-primary">Terms</Link></li>
                <li><Link to="/cookies" className="hover:text-primary">Cookies</Link></li>
                <li><a href="mailto:support@ruleai.systems" className="hover:text-primary" data-testid="footer-support-email">Support</a></li>
              </ul>
            </div>
          </div>
          <div className="mt-10 pt-6 border-t border-border text-[10px] font-mono-plex uppercase tracking-widest text-muted-foreground flex flex-wrap items-center justify-between gap-3">
            <div>© 2026 RuleAI.Systems &middot; All rights reserved</div>
            <div>v1.0 beta &middot; <a href="https://ruleai.systems" className="hover:text-primary">ruleai.systems</a></div>
          </div>
        </div>
      </footer>

      {/* Book 15-min with Scott — dialog */}
      <Dialog open={bookOpen} onOpenChange={(v) => { if (!v) closeBooking(); else setBookOpen(true); }}>
        <DialogContent className="sm:max-w-lg rounded-sm border-border" data-testid="booking-dialog">
          {!bookDone ? (
            <>
              <DialogHeader>
                <div className="overline text-accent">◉ 15 MINUTES · NO PITCH</div>
                <DialogTitle className="font-display text-2xl font-bold tracking-tight">
                  Book time with Scott
                </DialogTitle>
                <p className="text-sm text-muted-foreground pt-1">
                  Tell me about your ops problems. I&rsquo;ll bring product context if useful, but the call is for you.
                </p>
              </DialogHeader>
              <form onSubmit={submitBooking} className="space-y-3 pt-2">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="overline text-muted-foreground block mb-1.5">Name</label>
                    <Input
                      value={bookForm.name}
                      onChange={(e) => setBookForm({ ...bookForm, name: e.target.value })}
                      placeholder="Your name"
                      className="rounded-sm"
                      data-testid="booking-name-input"
                      required
                    />
                  </div>
                  <div>
                    <label className="overline text-muted-foreground block mb-1.5">Email</label>
                    <Input
                      type="email"
                      value={bookForm.email}
                      onChange={(e) => setBookForm({ ...bookForm, email: e.target.value })}
                      placeholder="you@company.com"
                      className="rounded-sm"
                      data-testid="booking-email-input"
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="overline text-muted-foreground block mb-1.5">Company</label>
                    <Input
                      value={bookForm.company}
                      onChange={(e) => setBookForm({ ...bookForm, company: e.target.value })}
                      placeholder="Company name"
                      className="rounded-sm"
                      data-testid="booking-company-input"
                    />
                  </div>
                  <div>
                    <label className="overline text-muted-foreground block mb-1.5">Role</label>
                    <Input
                      value={bookForm.role}
                      onChange={(e) => setBookForm({ ...bookForm, role: e.target.value })}
                      placeholder="e.g. Ops Manager"
                      className="rounded-sm"
                      data-testid="booking-role-input"
                    />
                  </div>
                </div>
                <div>
                  <label className="overline text-muted-foreground block mb-1.5">Best time to call</label>
                  <Input
                    value={bookForm.preferred_time}
                    onChange={(e) => setBookForm({ ...bookForm, preferred_time: e.target.value })}
                    placeholder="e.g. Weekday mornings CT · Tue/Thu after 2pm"
                    className="rounded-sm"
                    data-testid="booking-time-input"
                  />
                </div>
                <div>
                  <label className="overline text-muted-foreground block mb-1.5">What&rsquo;s on your mind?</label>
                  <Textarea
                    value={bookForm.message}
                    onChange={(e) => setBookForm({ ...bookForm, message: e.target.value })}
                    placeholder="Rough idea of what you&rsquo;d like to discuss — SOPs, quality, HR, scale, all of it welcome."
                    rows={4}
                    className="rounded-sm resize-none"
                    maxLength={2000}
                    data-testid="booking-message-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={bookLoading}
                  className="w-full h-11 rounded-sm bg-accent hover:bg-accent/90 text-white"
                  data-testid="booking-submit-btn"
                >
                  {bookLoading ? "Sending…" : (<>Request time <ArrowRight className="w-4 h-4 ml-2" /></>)}
                </Button>
                <p className="text-[10px] font-mono-plex uppercase tracking-widest text-muted-foreground text-center pt-1">
                  ◆ Scott replies personally within 24 hours
                </p>
              </form>
            </>
          ) : (
            <div className="py-6 text-center" data-testid="booking-success">
              <div className="w-14 h-14 mx-auto rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center">
                <CheckCircle2 className="w-7 h-7 text-emerald-600" />
              </div>
              <div className="font-display font-bold text-2xl mt-4 tracking-tight">Request received.</div>
              <p className="text-sm text-muted-foreground mt-3 max-w-sm mx-auto">
                Scott will reply from his personal email within 24 hours with a scheduling link.
                Check <strong className="text-foreground">{bookForm.email || "your inbox"}</strong> — sometimes the first email lands in Promotions.
              </p>
              <Button
                onClick={closeBooking}
                className="mt-6 rounded-sm bg-primary hover:bg-primary/90"
                data-testid="booking-close-btn"
              >
                Close
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Exit-intent modal — last-chance capture */}
      {showExit && !leadDone && !user && (
        <div
          className="fixed inset-0 z-[100] bg-zinc-950/80 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200"
          onClick={() => { setShowExit(false); setExitDismissed(true); }}
          data-testid="exit-intent-modal"
        >
          <div
            className="relative bg-card border border-border rounded-sm max-w-lg w-full p-8 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => { setShowExit(false); setExitDismissed(true); }}
              className="absolute top-3 right-3 text-muted-foreground hover:text-foreground"
              data-testid="exit-close-btn"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="overline text-accent">◉ WAIT — ONE THING</div>
            <h3 className="mt-3 font-display font-black text-3xl tracking-tight">
              Leaving without the free templates?
            </h3>
            <p className="text-sm text-muted-foreground mt-3">
              Grab the <strong className="text-foreground">SOP Starter Pack</strong> — 10 production-ready
              templates our founding customers use daily. Instant email. No signup.
            </p>
            <form onSubmit={(e) => submitLead(e, "exit_intent")} className="mt-5">
              <Input
                type="email"
                placeholder="you@company.com"
                value={leadEmail}
                onChange={(e) => setLeadEmail(e.target.value)}
                className="rounded-sm h-11"
                data-testid="exit-email-input"
                required
              />
              <Button
                type="submit"
                disabled={leadLoading}
                className="w-full mt-3 rounded-sm bg-accent hover:bg-accent/90 text-white h-11"
                data-testid="exit-submit-btn"
              >
                {leadLoading ? "Sending…" : (<>Send me the pack <ArrowRight className="w-4 h-4 ml-2" /></>)}
              </Button>
            </form>
            <div className="mt-4 text-center">
              <button
                onClick={() => { setShowExit(false); setExitDismissed(true); }}
                className="text-xs font-mono-plex uppercase tracking-widest text-muted-foreground hover:text-foreground"
                data-testid="exit-decline-btn"
              >
                No thanks, I&rsquo;ll figure it out myself
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
